/**
 * @fileoverview `<SocialIcon>` — a social platform's mark, drawn by its render policy
 * (LIFE-SHORTLISTED-001 P9.2, U33).
 *
 * Decoration only: the link that carries it holds the accessible name (a visible label or an
 * `sr-only` one), so a path mark is an `aria-hidden` SVG that is never focusable, and a brand-image
 * mark is an `aria-hidden` box of images with empty `alt`. A platform outside the closed
 * `SOCIAL_PLATFORM` set renders nothing — the label still reads, and no broken mark ships.
 *
 * A `brand-image` platform (LinkedIn) renders EVERY variant, each tagged with the ground it is drawn
 * for (`data-ground`); `@puffin-core/shared-styles` shows the one the ground asks for, because only the
 * page knows its ground (a tenant's ink is a CSS token, judged once by `applyTenantBranding`).
 *
 * @module @puffin-core/marketing-shell/social/SocialIcon
 */

import PropTypes from 'prop-types';

import { SOCIAL_ICON_KIND, SOCIAL_ICON_POLICY, SOCIAL_MARK_GROUND } from './socialIcons.js';

/** A brand-image policy's variant key → the ground its image is drawn for. */
const GROUND_OF_VARIANT = Object.freeze({
  onDark: SOCIAL_MARK_GROUND.DARK,
  onLight: SOCIAL_MARK_GROUND.LIGHT,
});

/**
 * @param {Object} props
 * @param {Readonly<{variants: Object<string, string>, width: number, height: number}>} props.policy
 * @returns {React.JSX.Element}
 */
function BrandImageMark({ policy }) {
  return (
    <span className="social-mark" aria-hidden="true">
      {Object.entries(policy.variants).map(([variant, src]) => (
        <img
          key={variant}
          className="social-mark-image"
          data-ground={GROUND_OF_VARIANT[variant]}
          src={src}
          alt=""
          width={policy.width}
          height={policy.height}
          loading="lazy"
        />
      ))}
    </span>
  );
}

BrandImageMark.propTypes = {
  policy: PropTypes.shape({
    variants: PropTypes.objectOf(PropTypes.string).isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
};

/**
 * @param {Object} props
 * @param {string} props.platform - one of `SOCIAL_PLATFORM`
 * @returns {React.JSX.Element|null}
 */
export function SocialIcon({ platform }) {
  if (!Object.hasOwn(SOCIAL_ICON_POLICY, platform)) {
    return null;
  }
  const policy = SOCIAL_ICON_POLICY[platform];
  if (policy.kind === SOCIAL_ICON_KIND.BRAND_IMAGE) {
    return <BrandImageMark policy={policy} />;
  }
  return (
    <svg
      className="social-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={policy.path} fill={policy.colour} />
    </svg>
  );
}

SocialIcon.propTypes = {
  platform: PropTypes.string.isRequired,
};

export default SocialIcon;
